
import { SelectVM, UserVM } from '@/models'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Multiselect from 'vue-multiselect'
import {
  getInsurances,
  getIPANumbers
} from '@/components/insurances/insurance.service'
import {
  getGenders,
  getInsuranceSupervisorInsurances
} from '@/components/doctors/doctors.service'
import moment from 'moment'

@Component({
  name: 'InsuranceSupervisorForm',
  components: {
    Multiselect
  }
})
export default class InsuranceSupervisorForm extends Vue {
  @Prop() form: UserVM
  @Prop() editMode: boolean

  public insuranceList: SelectVM[] = []
  public selectedInsurances: SelectVM[] = []
  public allInsuranceIPAList: SelectVM[] = []
  public filteredInsuranceIPAList: SelectVM[] = []
  public selectedInsuranceIPAs: SelectVM[] = []
  public selectedGender: string | null = null
  public genderList: SelectVM[] = [
    { value: null, label: 'Select a gender', _isDisabled: false }
  ]
  public formatted = ''
  public confirmation = ''

  @Watch('selectedInsurances')
  onSelectedInsurances() {
    this.applyInsuranceFilter()
  }

  @Watch('selectedInsuranceIPAs', { deep: true })
  onSelectedInsuranceIPAs() {
    this.syncInsuranceIPAs()
  }

  public syncInsuranceIPAs() {
    this.form.insuranceIPA = (this.selectedInsuranceIPAs || [])
      .map((i: SelectVM) => String(i.label || '').trim())
      .filter(Boolean)
      .join(',')
    this.form.insurances = null
    this.form.insuranceID = null
    this.form.ipaNumber = null
  }

  @Watch('selectedGender')
  onSelectedGender() {
    this.form.sex = this.genderList.find(
      c => c.value == this.selectedGender
    )?.label
  }

  public async created() {
    this.form.role = 'InsuranceSupervisor'

    await Promise.all([
      this.fetchInsurances(),
      this.fetchInsuranceIPAs(),
      this.fetchGenders()
    ])

    if (this.editMode) {
      this.setGender()
      this.setDateOfBirth()
      this.confirmation = this.form.email1
      await this.setInsuranceIPAs()
    }
  }

  public setDateOfBirth() {
    this.formatted = moment(this.form.dob).format('MM/DD/YYYY')
  }

  public setGender() {
    this.selectedGender = this.genderList.find(
      c => c.label == this.form.sex
    )?.value
  }

  public async setInsuranceIPAs() {
    if (!this.form.rmT_UserId) return

    const assigned = await getInsuranceSupervisorInsurances(
      this.form.rmT_UserId
    )
    const assignedKeys = new Set(
      assigned.map(a => `${a.ipaNumber},${a.insuranceID}`)
    )
    const assignedLabels = new Set(
      assigned.map(a => a.insuranceIPA).filter(Boolean)
    )

    this.selectedInsuranceIPAs = this.allInsuranceIPAList.filter(
      opt => assignedKeys.has(opt.value) || assignedLabels.has(opt.label)
    )

    const insuranceIds = new Set(assigned.map(a => a.insuranceID.toString()))
    this.selectedInsurances = this.insuranceList.filter(i =>
      insuranceIds.has(i.value)
    )
  }

  public async fetchInsurances() {
    const response = await getInsurances(1, 1500)
    this.insuranceList = (response.data || []).map(i => ({
      label: i.insuranceName,
      value: i.insuranceID.toString(),
      _isDisabled: false
    }))
  }

  public async fetchInsuranceIPAs() {
    const response = await getIPANumbers()
    this.allInsuranceIPAList = (response.data || []).map(i => ({
      label: i.insuranceIPA,
      value: `${i.ipaNumber},${i.insuranceID}`,
      _isDisabled: false
    }))
    this.applyInsuranceFilter()
  }

  public applyInsuranceFilter() {
    if (!this.selectedInsurances.length) {
      this.filteredInsuranceIPAList = this.allInsuranceIPAList
      return
    }

    const selectedIds = new Set(
      this.selectedInsurances.map(i => i.value.toString())
    )
    this.filteredInsuranceIPAList = this.allInsuranceIPAList.filter(i => {
      const insuranceId = i.value.split(',')[1]
      return selectedIds.has(insuranceId)
    })
  }

  public async fetchGenders() {
    const response = await getGenders()
    this.genderList.push(
      ...response.map(g => ({
        label: g.label,
        value: g.value.toString(),
        _isDisabled: false
      }))
    )
  }

  public selectAllInsurances() {
    const all = (this.insuranceList || []).map(i => ({ ...i }))
    this.selectedInsurances = []
    this.$nextTick(() => {
      this.selectedInsurances = all
    })
  }

  public selectAllInsuranceIPAs() {
    const selectedValues = new Set(
      (this.selectedInsuranceIPAs || []).map(i => String(i.value))
    )
    const toAdd = (this.filteredInsuranceIPAList || [])
      .filter(i => !selectedValues.has(String(i.value)))
      .map(i => ({ ...i }))
    this.selectedInsuranceIPAs = [
      ...(this.selectedInsuranceIPAs || []),
      ...toAdd
    ]
  }

  public onDOBContext(ctx) {
    this.formatted = moment(ctx).format('MM/DD/YYYY')
  }

  public onDOBInput(ctx) {
    const a = moment(ctx).toDate()
    if (a.toString() != 'Invalid Date') this.form.dob = a
  }

  public getLength(value) {
    return value ? value.toString().trim().length : 0
  }
}
